import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ScanPanelComponent } from '../../shared/components/scan-panel/scan-panel.component';
import { ApiService } from '../../core/services/api.service';
import { NotificationService } from '../../core/notifications/notification.service';
import {
  QrReaderService,
  QrScanLogPayload,
} from '../../core/qr/qr-reader.service';
import { VisitService } from '../../core/visit/visit.service';
import { NotifiedPersonContact, QrScanErrorType } from '../../core/visit/visit.types';
import { TokenService } from '@core/auth/token.service';

interface QrHost {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

interface QrVisitor {
  firstName?: string;
  lastName?: string;
  company?: string;
  email?: string;
  tag?: string;
}

interface VerifyQrResponse {
  status: boolean;
  data?: {
    visitor?: QrVisitor;
    host?: QrHost;
  };
}

/** Error body returned by /visit/verify-qr (BaseController::error shape). */
interface VerifyQrErrorBody {
  errorCode?: string;
  errors?: {
    code?: string | null;            // early | late | cancelled | …
    label?: string | null;
    appointmentDate?: number | null; // Unix seconds
    visitor?: QrVisitor;
    host?: QrHost;
    // ALREADY_CHECKED_IN: l'arrivée déjà en base pour ce code scanné.
    id_session?: number;
    firstname?: string | null;
    lastname?: string | null;
    checkin_at?: string | null;      // heure serveur, `2026-09-16 16:20:04`
  };
}

@Component({
  selector: 'app-scan-qr',
  standalone: true,
  imports: [ScanPanelComponent],
  templateUrl: './scan-qr.component.html',
  styleUrl: './scan-qr.component.scss',
})
export class ScanQrComponent implements OnInit {
  private readonly router        = inject(Router);
  private readonly api           = inject(ApiService);
  private readonly visit         = inject(VisitService);
  private readonly notifications = inject(NotificationService);
  private readonly destroyRef    = inject(DestroyRef);
  private readonly tokenService  = inject(TokenService);
  private readonly qrReader      = inject(QrReaderService);

  /**
   * Vrai dès qu'une issue a été retenue. La lecture est désormais à usage unique
   * (`QrReaderService.read()` n'émet qu'une fois) : ce drapeau ne sert plus qu'à
   * départager les échecs de la phase de **vérification**.
   */
  private resolved = false;

  ngOnInit(): void {
    // Retour depuis « Arrivée déjà signalée » : cet écran a clôturé et enregistré
    // la session. Le scan suivant est un nouveau visiteur, il en ouvre une.
    if (!this.visit.current()) {
      this.visit.restartQrPassage();
    }

    this.qrReader
      .read()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((outcome) => {
        if ('scan' in outcome) {
          this.verify(outcome.scan);
          return;
        }
        this.handleFailure(outcome.failure, {
          statut_lecteur: 0,
          connexion_api: 0,
          statut_code: 0,
          erreur:
            outcome.failure === 'no_reader' ? 'Le lecteur ne répond pas' : 'QR code non lu',
          context: 'CHECKIN',
        });
      });
  }

  /** Vérifie le code auprès de WWS via l'API. */
  private verify(scan: string): void {
    this.resolved = true;
    this.api
      .post<VerifyQrResponse>('/visit/verify-qr', {
        scan,
        macAddress: this.tokenService.mac ?? '',
      })
      .subscribe({
        next: (res) => this.handleVerifySuccess(scan, res),
        error: (err) => this.handleVerifyError(scan, err),
      });
  }

  private handleVerifySuccess(scan: string, res: VerifyQrResponse): void {
    const visitor = res?.data?.visitor;
    if (!visitor || (!visitor.firstName && !visitor.lastName)) {
      this.handleFailure('not_recognized', {
        statut_lecteur: 1,
        connexion_api: 1,
        statut_code: 0,
        erreur: 'QR code non reconnu',
        context: 'CHECKIN',
      });
      return;
    }

    this.qrReader.logScanAttempt({
      statut_lecteur: 1,
      connexion_api: 1,
      statut_code: 1,
      erreur: null,
      context: 'CHECKIN',
    });

    const visitorInfo = {
      firstName: visitor.firstName,
      lastName: visitor.lastName,
      company: visitor.company,
      email: visitor.email,
    };
    const hostContact = this.buildHostContact(res?.data?.host);

    this.visit.updateVisitor(visitorInfo);
    this.visit.updateFlow({
      entryMethod: 'qr',
      qrScannedData: scan,
      qrScanError: false,
      qrScanErrorType: undefined,
      qrAppointmentDate: null,
      notifiedPersonName: this.buildHostDisplayName(res?.data?.host),
      notifiedPersonHost: hostContact,
    });

    if (hostContact) {
      this.notifications.notifyHost(visitorInfo, hostContact);
    }

    this.visit.trackEvent('appointment_with');

    void this.router.navigate(['/visitor-welcome']);
  }

  private buildHostContact(host: QrHost | undefined): NotifiedPersonContact | undefined {
    if (!host) return undefined;
    const contact: NotifiedPersonContact = {
      firstName: host.firstName,
      lastName: host.lastName,
      email: host.email,
      phone: host.phone,
    };
    return contact.firstName || contact.lastName ? contact : undefined;
  }

  private handleVerifyError(scan: string, err: unknown): void {
    const httpErr = err as { status?: number; error?: VerifyQrErrorBody };
    if (httpErr?.status === 409 && httpErr.error?.errorCode === 'ALREADY_CHECKED_IN') {
      this.handleAlreadyCheckedIn(scan, httpErr.error);
      return;
    }
    if (httpErr?.status === 404) {
      // A recognized pass outside its valid window (early/late/canceled) comes
      // back as QR_NOT_CONFIRMED; everything else 404 is an unknown QR code.
      if (httpErr.error?.errorCode === 'QR_NOT_CONFIRMED') {
        this.handleNotConfirmed(scan, httpErr.error);
        return;
      }
      this.handleFailure('not_recognized', {
        statut_lecteur: 1,
        connexion_api: 1,
        statut_code: 0,
        erreur: 'QR code non reconnu',
        context: 'CHECKIN',
      });
      return;
    }
    this.handleFailure('api_unreachable', {
      statut_lecteur: 1,
      connexion_api: 0,
      statut_code: 0,
      erreur: 'Erreur de connexion',
      context: 'CHECKIN',
    });
  }

  /**
   * Rescan d'un pass dont l'arrivée du jour est déjà enregistrée et non clôturée
   * par un départ.
   *
   * Deux règles portent tout le comportement :
   *  - **La session est enregistrée comme un refus** : l'écran d'erreur la clôture
   *    et l'envoie à `/visit/save`. Sa ligne QR porte `qr_scan_error =
   *    already_checked_in` et le code scanné : les recherches de départ et le
   *    contrôle de doublon exigent `qr_scan_error IS NULL`, elle n'est donc jamais
   *    prise pour l'arrivée. Le nom n'est pas repris de la visite existante, pour
   *    que la recherche de départ par nom ne liste pas le visiteur deux fois.
   *  - **Aucune nouvelle tentative** : rescanner redonnerait la même réponse, et
   *    la bascule manuelle créerait justement le doublon qu'on refuse. Le
   *    compteur de tentatives n'est donc pas incrémenté (cf. le « départ déjà
   *    enregistré » du parcours de départ, SPECS §4.1.2).
   *
   * L'hôte n'est pas re-notifié : il l'a été au premier scan.
   */
  private handleAlreadyCheckedIn(scan: string, body: VerifyQrErrorBody): void {
    this.visit.updateFlow({
      qrScannedData: scan,
      qrAlreadyCheckedIn: {
        firstName: body.errors?.firstname ?? null,
        lastName: body.errors?.lastname ?? null,
        checkinAt: body.errors?.checkin_at ?? null,
      },
    });

    // Le code a bien été lu ET reconnu : seule la règle métier le refuse, d'où
    // `statut_code: 1`. Il compte pour la fiabilité du lecteur.
    this.handleFailure(
      'already_checked_in',
      {
        statut_lecteur: 1,
        connexion_api: 1,
        statut_code: 1,
        erreur: 'Arrivée déjà signalée',
        context: 'CHECKIN',
      },
      false,
    );
  }

  /** Map the QR_NOT_CONFIRMED reason to an error screen + keep the RDV date. */
  private handleNotConfirmed(scan: string, body: VerifyQrErrorBody): void {
    // WWS status.code is e.g. early | late | cancelled. Match "cancel*" by prefix
    // so both "canceled" and "cancelled" spellings resolve to the same type.
    const code = (body.errors?.code ?? '').toLowerCase();
    const type: QrScanErrorType =
      code === 'early' ? 'early'
      : code === 'late' ? 'late'
      : code.startsWith('cancel') ? 'cancelled'
      : 'not_recognized';

    // Access is denied, but the pass WAS recognized: we know who the visitor is,
    // who the appointment host is, and we hold the scanned code. Persist all three
    // so the saved visit records a recognized-but-denied appointment:
    //   - wh_visits     ← the visitor (lastname/firstname/email/company)
    //   - wh_visit_host ← the RDV host (lastname/firstname/email)
    //   - the visitor's qr_scan_attempt history row ← scanned_data + the error code
    // A later manual fallback that selects another host overwrites notifiedPersonHost,
    // so wh_visit_host reflects the host the visit ended on. We do NOT notify the host
    // here — a denied visitor is not announced.
    const visitor = body.errors?.visitor;
    if (visitor && (visitor.firstName || visitor.lastName)) {
      this.visit.updateVisitor({
        firstName: visitor.firstName,
        lastName: visitor.lastName,
        company: visitor.company,
        email: visitor.email,
      });
    }
    this.visit.updateFlow({
      qrAppointmentDate: body.errors?.appointmentDate ?? null,
      qrScannedData: scan,
      notifiedPersonName: this.buildHostDisplayName(body.errors?.host),
      notifiedPersonHost: this.buildHostContact(body.errors?.host),
    });
    this.handleFailure(type, {
      statut_lecteur: 1,
      connexion_api: 1,
      statut_code: 0,
      erreur: `RDV non confirmé (${code || 'inconnu'})`,
      context: 'CHECKIN',
    });
  }

  /** Failures driven by the verify-qr API response (allowed after `resolved`). */
  private static readonly API_RESULT_TYPES: ReadonlySet<QrScanErrorType> = new Set<QrScanErrorType>([
    'not_recognized',
    'api_unreachable',
    'early',
    'late',
    'cancelled',
    'already_checked_in',
  ]);

  /**
   * @param countsAsAttempt Une 2e tentative propose l'identification manuelle
   *   (SPECS §4.2). Faux quand réessayer ne peut rien changer.
   */
  private handleFailure(
    type: QrScanErrorType,
    log: QrScanLogPayload,
    countsAsAttempt = true,
  ): void {
    if (this.resolved && !ScanQrComponent.API_RESULT_TYPES.has(type)) {
      return;
    }
    this.resolved = true;

    this.qrReader.logScanAttempt(log);

    this.visit.updateFlow({
      qrScanError: true,
      qrScanErrorType: type,
    });
    this.visit.trackEvent('qr_scan_error', { type });

    if (!countsAsAttempt) {
      void this.router.navigate(['/qr-error']);
      return;
    }

    const attempts = this.visit.incrementQrAttempt();
    const next = attempts >= 2 ? '/qr-error-manual' : '/qr-error';
    void this.router.navigate([next]);
  }

  private buildHostDisplayName(host: QrHost | undefined): string | undefined {
    if (!host) return undefined;
    const parts = [host.firstName, host.lastName].filter(Boolean);
    return parts.length ? parts.join(' ') : undefined;
  }

}
